// ─────────────────────────────────────────────────────────────────────────────
// REQ-109 — Pantalla "Brillamos Juntos" (/app/brillamos).
//
// LÓGICA DE LANDING: la pantalla es SIEMPRE el home ShineTogether (starfield, hero, racha,
// constelación) — igual que el prototipo, que enruta "Brillamos Juntos" a
// `features/home/ShineTogetherHomePage.tsx` sin ninguna condición de campaña. Con campaña activa
// se añaden encima las secciones que dependen de ella: documentos, leaderboard, panel de
// reconocer y muro.
//
// CAMBIO SOBRE REQ-116 (2026-08-03, a petición del cliente): sin campaña activa esta pantalla YA
// NO cae a la Librería de Fundamentales. Esa librería tiene su propia entrada de menú
// (`/app/fundamentales`, `FundamentalesView`) y verla también aquí no corresponde al prototipo.
//
// CAMBIO (2026-08-10, a petición del cliente): se retira el bloque "Feed de actividad"
// ("Mis Nominaciones" + "Todas las nominaciones") de esta landing. Salía SIEMPRE que había
// campaña activa porque su interruptor (`feedActividad` en Vistas de Usuario) nace en `true` por
// defecto (ver `USER_VIEWS_DEFAULT`, api/recognition/types.ts) y nadie lo había apagado. Sus dos
// CTA de vacío ("Sé el primero en nominar" / "Nominar ahora") llevaban a `/app/nominar`
// (`NominarView`), la pantalla de creación aparte que el cliente ya había pedido sacar del CTA
// del hero el 2026-08-04 por el mismo motivo (percibida como modal ajeno al home) pero que
// seguía colándose por aquí. El componente `BrillamosFeed.tsx` se BORRÓ del árbol (no solo dejó
// de renderizarse aquí); solo queda oculto el campo `feedActividad` del contrato del backend
// (patrón `VISTAS_SIN_SUPERFICIE` de `AdminVistas.tsx`), porque a diferencia del componente sí
// forzaría una migración de datos. `/app/nominar` sigue vivo porque `InnovacionView` lo usa a
// propósito como alternativa secundaria (ver su comentario "punto 6").
//
// REQ-109: datos de campaña REALES (useActiveCampaign + useRecognitionCatalog). Los PUNTOS jamás
// se muestran. Estados loading/error reales.
// ─────────────────────────────────────────────────────────────────────────────
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  useActiveCampaign, useRecognitionCatalog, useRecognitionCampaign,
  getCampaignName, pickLocalized, useLandingBanner, useRecognitionRules,
} from '@/api/recognition';
import type { CampaignDto, FundamentalDto } from '@/api/recognition';
import { useUserContext } from '@/contexts/UserContext';
import { useForceDarkMode } from '@/contexts/ThemeContext';
import { RecognitionShell, StateBlock } from './components';
import { BrillamosStars } from './BrillamosStars';
import { shineLogoSrc } from './shineLogo';
import { Constelacion } from './Constelacion';
import { CampaignDocs } from './CampaignDocs';
import { StreakWidget } from './StreakWidget';
import { useVistaHabilitada } from './useVistas';
import { ReconocerPanel } from './ReconocerPanel';
import { WordBuilderPanel } from './WordBuilderPanel';
import { PostItWall } from './PostItWall';
import { BulkUploadModal } from './bulkUpload/BulkUploadModal';
import { SparkleOverlay } from './SparkleOverlay';
import { useRecognitionPermissions } from './access/useRecognitionPermissions';
import { fundamentalesOfrecibles } from './postitShared';
import { OU_META } from './ouMeta';

const MEDALS = ['🥇', '🥈', '🥉'];

/**
 * REQ-117 — Leaderboard de nominaciones por OU/país (H-66: namespace y estética `.ou-ranking-*`
 * del demo, con banderas y medallas — ShineTogetherHomePage.tsx demo : 495-563). Reagrupa el
 * ranking de CampaignReportDto por su `ouName` (RF-149 — antes `etiqueta`, que el backend nunca
 * poblaba: esta sección quedaba siempre colapsada en un solo grupo "sin OU") y suma nominaciones.
 * Degradación honesta (P-05): la sección solo aparece cuando hay datos reales; si el endpoint de
 * analítica no está permitido para el usuario (403) o no hay ranking, no se muestra (sin datos
 * falsos, sin tarjetas de error).
 */
function CampaignOuLeaderboard({ campaignId, lang, userOu }: Readonly<{ campaignId: string; lang: string; userOu: string | undefined }>) {
  const { t } = useTranslation('recognition');
  // El endpoint de reporte de campaña exige la capacidad `Reporteria` y responde 403 a quien no la
  // tenga (correcto: es un endpoint de Reportería). Sin este gate la pantalla se lo pedía a
  // CUALQUIER empleado — un 403 por render, ensuciando la consola y gastando una petición para nada.
  // La degradación de más abajo (`return null`) ya era correcta; lo que faltaba era no preguntar.
  const { canViewReports } = useRecognitionPermissions('Brillamos');
  const q = useRecognitionCampaign({ campaignId, language: lang }, canViewReports);
  const report = q.data;

  const byOu = useMemo(() => {
    if (!report) return [];
    const map = new Map<string, number>();
    for (const r of report.ranking) {
      if (r.suprimido) continue;
      const key = (r.ouName ?? '').trim() || t('leaderboard.noOu');
      map.set(key, (map.get(key) ?? 0) + r.count);
    }
    return Array.from(map.entries())
      .map(([ou, count]) => ({ ou, count }))
      .sort((a, b) => b.count - a.count);
  }, [report, t]);

  // Solo se muestra con datos reales (evita tarjetas de error a empleados sin permiso de analítica).
  if (q.isLoading || q.isError || !report || byOu.length === 0) return null;

  const max = byOu[0]?.count || 1;

  return (
    <div className="dash-card shine-card ou-ranking-card">
      <div className="ou-ranking-header">
        <span className="ou-ranking-icon" aria-hidden="true">🏆</span>
        <div>
          <p className="ou-ranking-title">{t('leaderboard.title')}</p>
          <p className="ou-ranking-sub">{t('leaderboard.subtitle')}</p>
        </div>
      </div>
      <div className="ou-ranking-list">
        {byOu.map((row, i) => {
          const meta = OU_META[row.ou] || { label: row.ou, flag: '🌐', color: '#888' };
          const isMine = userOu === row.ou;
          const pct = Math.max(6, Math.round((row.count / max) * 100));
          return (
            <div key={row.ou} className={`ou-ranking-row ${isMine ? 'ou-ranking-row--mine' : ''}`}>
              <span className="ou-ranking-rank">{MEDALS[i] ?? `#${i + 1}`}</span>
              <span className="ou-ranking-flag">{meta.flag}</span>
              <span className="ou-ranking-name">
                {meta.label}
                {isMine && <span className="ou-ranking-you">{t('leaderboard.you')}</span>}
              </span>
              <span className="ou-ranking-bar-track">
                <span className="ou-ranking-bar-fill" style={{ width: `${pct}%`, background: meta.color }} />
              </span>
              <span className="ou-ranking-count">{row.count}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/** Toggle rápido ES/EN del hero de campaña (H-58, `.bj-lang-toggle`). El demo usa un `Globe` de
 * lucide-react; el paquete no está instalado en este repo (deuda ya documentada por F1,
 * CONTRATO-SHELL.md §8.1) — se usa el equivalente de Font Awesome 6.4 (`fa-globe`), mismo patrón
 * de esa fase. Cambia el idioma REAL de la app (i18next), no un locale local como el demo. */
/**
 * Home ShineTogether (REQ-109).
 *
 * `campaign` puede ser `null`: el prototipo pinta SIEMPRE esta pantalla en "Brillamos Juntos"
 * (hero + racha + constelación), y así lo pidió el cliente. Lo que depende de una campaña —
 * documentos, leaderboard, feed, panel de reconocer y muro — se monta solo cuando la hay; el
 * resto es independiente del dato de campaña y se pinta igual.
 */
/**
 * RN-PI03 — Sección "Reconocer" del landing de Brillamos con su modalidad de entrada PROPIA
 * (`brillamosEntryPointMode`): 'A' solo clásico, 'B' solo post-it, 'both' el usuario elige (muestra el
 * selector). Default 'A' = comportamiento histórico (Brillamos solo pintaba el clásico). Mismo patrón
 * que ReconocerView para Fundamentales; vive aparte para no inflar la complejidad de CampaignLanding.
 */
function BrillamosRecognizeSection({
  campaignId, canBulk, onOpenBulk, onSent,
}: Readonly<{
  campaignId: string | null;
  canBulk: boolean;
  onOpenBulk: () => void;
  onSent: () => void;
}>) {
  const { t } = useTranslation('recognition');
  // Pantalla de Brillamos: pide sus reglas del programa Brillamos, no del global de Fundamentales.
  const rules = useRecognitionRules(undefined, 'Brillamos');
  const entryMode = rules.data?.brillamosEntryPointMode ?? 'A';
  const [mode, setMode] = useState<'classic' | 'interactive'>('classic');
  useEffect(() => {
    if (entryMode === 'A' && mode !== 'classic') setMode('classic');
    if (entryMode === 'B' && mode !== 'interactive') setMode('interactive');
  }, [entryMode, mode]);

  // Con 'A'/'B' fijado el selector no se muestra y el ancla `#reconocer-section` va en el panel; con
  // 'both' el ancla va en la barra del selector para que el scroll caiga sobre él.
  const anchorOnSwitch = entryMode === 'both';
  return (
    <>
      {anchorOnSwitch && (
        <div id="reconocer-section" className="rec-mode-bar">
          <div className="rec-mode-switch" role="tablist" aria-label={t('builder.modeLabel')}>
            <button type="button" role="tab" aria-selected={mode === 'classic'}
              className={`rec-mode-opt ${mode === 'classic' ? 'rec-mode-opt--on' : ''}`} onClick={() => setMode('classic')}>
              <i className="fas fa-list-check" /> {t('builder.modeClassic')}
            </button>
            <button type="button" role="tab" aria-selected={mode === 'interactive'}
              className={`rec-mode-opt ${mode === 'interactive' ? 'rec-mode-opt--on' : ''}`} onClick={() => setMode('interactive')}>
              <i className="fas fa-wand-magic-sparkles" /> {t('builder.modeInteractive')}
            </button>
          </div>
        </div>
      )}
      {mode === 'classic' ? (
        <div id={anchorOnSwitch ? undefined : 'reconocer-section'} className="dash-card dash-card--form shine-card shine-recognize-panel">
          {/* P2-4 — el bloque de carga masiva (CSV) también va aquí; reglas en useRecognitionPermissions (D-5). */}
          <ReconocerPanel canBulk={canBulk} onOpenBulk={onOpenBulk} onSent={onSent} campaignId={campaignId} isBrillamosScreen />
        </div>
      ) : (
        <div id={anchorOnSwitch ? undefined : 'reconocer-section'} className="dash-card--interactive shine-card shine-recognize-panel">
          <WordBuilderPanel onSent={onSent} campaignId={campaignId} isBrillamosScreen />
        </div>
      )}
    </>
  );
}

function CampaignLanding({
  campaign, funds, catalogEmpty, lang, userId, userOu,
}: Readonly<{
  campaign: CampaignDto | null;
  funds: FundamentalDto[];
  catalogEmpty: boolean;
  lang: string;
  userId: string | undefined;
  userOu: string | undefined;
}>) {
  const { t } = useTranslation('recognition');
  // P2-4 — carga masiva por CSV en esta pantalla. Mismo cableado que ReconocerView: el panel pinta
  // la sección y el contenedor es dueño del modal.
  const { canBulk } = useRecognitionPermissions('Brillamos');
  // WP-F3 / CPE-158 — la OU decide si ofrece estas vistas. El hook compone además con la regla
  // propia de cada una: apagar aquí las oculta, encender no resucita lo que otra regla tapa.
  const verLeaderboard = useVistaHabilitada('leaderboard');
  // CPE-138 — «Mostrar Ranking» de la excepción de localidad, eje del VIEWER (RecognitionRulesDto,
  // mismo endpoint que ya usa `BrillamosRecognizeSection`). Se compone con AND sobre `verLeaderboard`
  // (mismo criterio "se ofrece Y su propia regla lo permite" de `useVistas.ts`), nunca lo sustituye:
  // una OU sin el toggle global no lo recupera aunque su excepción diga que sí.
  const brillamosRules = useRecognitionRules(undefined, 'Brillamos');
  const rankingVisible = brillamosRules.data?.rankingVisible ?? true;
  // Anti-Spoiler de la campaña Brillamos vigente de quien mira (decisión 9) — oculta el chip
  // «Nominaciones» del widget de racha (`totalNominaciones`). El muro de «Recibidos» ya no se
  // desmonta: el servidor quita de él lo protegido y `PostItWall` avisa.
  const antiSpoilerOn = Boolean(brillamosRules.data?.antiSpoilerActivoAhora);
  const verFundamentales = useVistaHabilitada('vistaFundamentales');
  // Bug reportado: apagar un Fundamental en "Fundamentales disponibles" (Gestión → Brillamos) no
  // lo quitaba de la Constelación. `Constelacion` solo filtraba por `f.isActive` (la baja GLOBAL
  // del catálogo de Pulso) — nunca miraba `fundamentalesDeshabilitados` (la lista de esta pantalla,
  // que sí llega aquí vía `brillamosRules`). Mismo filtro que ya usa el riel de `ReconocerView`
  // (`railFunds`, bug #21) — `fundamentalesOfrecibles` aplica las dos condiciones a la vez.
  const constelacionFunds = useMemo(
    () => fundamentalesOfrecibles(funds, brillamosRules.data?.fundamentalesDeshabilitados),
    [funds, brillamosRules.data?.fundamentalesDeshabilitados],
  );
  /**
   * BUG-119 (UAT Sesión 2 Europa) — RN-C06 leída CON su anotación resaltada en amarillo en la
   * Especificación Funcional: «La sección "Reconocimientos recientes [solo enviados los recibidos
   * si es dependiente]" en el landing de campaña es independiente de la configuración de
   * visibilidad de enviados/recibidos del Paso 5».
   *
   * La independencia que RN-C06 concede es para ENVIADOS. Los RECIBIDOS sí dependen del Paso 5, y
   * hasta aquí no lo hacían: apagar «Mostrar contador de recibidos» ocultaba el histórico de Mi
   * Perfil y dejaba este bloque a la vista, así que el spoiler que el toggle pretende evitar seguía
   * a un clic de distancia.
   *
   * 🔴 El gate vive en el FRONT y no en el backend a propósito. `GetFeedQueryHandler` ignora estos
   * toggles en cuanto la consulta trae `CampanaId` —decisión D-6, blindada por
   * `PerfilTogglesTests.ElLandingDeLaCampana_NoAplicaLosToggles_NiSiquieraAcotadoPorPersona`— y este
   * bloque SIEMPRE lo trae cuando hay campaña. Levantar ese gate allí afectaría a la landing de
   * todas las campañas y también a «Enviados», que RN-C06 sí declara independiente.
   *
   * Sin campaña se muestra: no hay Paso 5 que consultar, y en ese caso la consulta sale sin
   * `campaignId`, así que el filtro de D-6 en el backend ya aplica los toggles por su cuenta.
   *
   * `!== false` con encadenamiento opcional, y no `campaign.visibilidad.recibidos` a secas, por una
   * razón de PRUEBAS y no de datos: la API nunca manda una campaña sin `visibilidad` (la entidad la
   * inicializa con `= new()` y el DTO la declara obligatoria), pero varios fixtures de
   * `brillamosShellParity.test.tsx` construyen campañas mínimas sin ese campo, y desreferenciarlo
   * los tumbaba enteros. Ausente ⇒ se muestra, que es el default del DTO (`Recibidos = true`).
   * No leerlo como que el campo sea opcional: no lo es.
   */
  const verRecibidosRecientes = campaign?.visibilidad?.recibidos !== false;
  const [showBulk, setShowBulk] = useState(false);
  const [showSparkle, setShowSparkle] = useState(false);
  const onOpenBulk = () => setShowBulk(true);
  const active = campaign?.estado === 'Activa';
  // REQ-115 — campaña Personalizada: SOLO landing informativa (documento/microsite), sin muro de
  // nominación ni botón de nominar. La nominación aplica a las campañas Brillamos.
  const isPersonalizada = campaign?.tipo === 'Personalizada';
  const description = campaign ? pickLocalized(campaign.descripcion, lang) : '';
  // Banner PROPIO de Brillamos (config de la pestaña Brillamos → GestionSettings.brillamosBanner*, GET no-admin).
  // NO es el de Fundamentales: cada programa tiene su banner. Fallback por-campo al logo shine / subtítulo por
  // defecto si no hay config o el endpoint falla. La descripción de una campaña Personalizada tiene precedencia.
  const brillamosBanner = useLandingBanner(lang).data;
  const brillamosSubtitle = pickLocalized(brillamosBanner?.brillamosBannerSubtitle, lang) || t('home.subtitle');
  // QA (2026-08-27) — logo: lectura EXACTA del idioma activo, NO `pickLocalized` (que cae en cascada
  // a es → en → la primera clave disponible). `BannerEditorFields` (adminShared.tsx) sube el logo por
  // idioma con esa misma semántica exacta («logo mostrado en la preview cuando el idioma activo no
  // tiene uno propio subido»): un admin que solo cargó el logo en español no debe pisar el wordmark
  // propio de los demás idiomas (`shineLogoSrc`, más abajo) — si se usara `pickLocalized` aquí, ese
  // único logo en español se devolvería para CUALQUIER idioma sin logo propio y el selector de
  // idioma dejaría de cambiar el logo, el bug reportado por QA.
  const brillamosLogo = brillamosBanner?.brillamosBannerLogoUrl?.[lang.split('-')[0].toLowerCase()];

  // A petición del cliente (2026-08-04): el CTA del hero ya no navega a /app/nominar (pantalla
  // aparte, percibida como un modal ajeno al home). En vez de eso baja hasta el panel de
  // "Reconoce a tus compañeros" — que ya vive en esta misma pantalla, ver `ReconocerPanel` más
  // abajo — y deja el foco listo en el buscador de personas. Mismo patrón que `scrollToForm` en
  // ReconocerView.tsx : 84.
  const onRecognizeClick = () => {
    document.getElementById('reconocer-section')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    window.setTimeout(() => {
      document.getElementById('reconocer-recipients-input')?.focus();
    }, 400);
  };

  return (
    <>
      {/* Fila superior: banner y racha son dos tarjetas HERMANAS en un grid 1.45fr/1fr, no una
          tarjeta con la racha anidada (demo ShineTogetherHomePage.tsx : 624-644, `.shine-top-row`). */}
      <div className="shine-top-row">
        <div className="dash-card shine-card shine-hero-banner">
          <img
            src={brillamosLogo || shineLogoSrc(lang)}
            alt={campaign ? getCampaignName(campaign, lang) : t('home.heroLogoAlt')}
            className="shine-hero-logo-banner"
          />

          <div className="shine-banner-footer">
            {/* `home.subtitle` y `home.cta` son las cadenas LITERALES del prototipo
                (`home.subtitle` y `shine.cta` de su AppContext.tsx), ya portadas para el hero de
                /app/reconocer. Antes esta pantalla usaba `home.heroSubtitle`/`home.nominateNow`,
                redacción propia del integrado que no existe en la maqueta. */}
            <p className="shine-subtitle">{isPersonalizada && description ? description : brillamosSubtitle}</p>
            {/* Botón de reconocer: nunca en campaña Personalizada, y si HAY campaña solo cuando está
                activa (una campaña finalizada no invita a reconocer). Sin campaña se pinta igual.
                No navega — baja hasta `#reconocer-section` en esta misma pantalla, ver
                `onRecognizeClick` arriba.
                `.dash-hero-cta.shine-cta` es la combinación del prototipo (demo : 635-640):
                degradado violeta, radio 14px y 14/28 de padding. Antes llevaba `.bj-nominar-btn`
                —heredado de `BrillamosJuntosPage`— que lo dejaba en azul plano y sin radio.
                Sin `<img>`: el prototipo pinta solo texto y la chispa ya va dentro de la cadena
                (`✨ Reconocer a alguien`). El icono era del `.bj-nominar-btn` heredado. */}
            {(!campaign || active) && !isPersonalizada && (
              <button type="button" className="dash-hero-cta shine-cta" onClick={onRecognizeClick}>
                {t('home.cta')}
              </button>
            )}
          </div>
        </div>

        <StreakWidget programa="Brillamos" isAntiSpoilerOn={antiSpoilerOn} />
      </div>

      {campaign && isPersonalizada ? (
        // REQ-115 — landing personalizada: documento(s)/microsite de la campaña, sin muro de nominación.
        // R8b (Tarea 15) — `programa="Brillamos"`: una excepción de "Campañas por OU" es siempre por
        // PROGRAMA (nunca por el tipo de campaña vigente), así que aplica igual aunque esta campaña
        // en particular sea Personalizada.
        <>
          <CampaignDocs campaignId={campaign.id} programa="Brillamos" isPersonalizada={isPersonalizada} allowCorporateFallback={false} />
          <p className="bj-custom-note"><i className="fas fa-circle-info" aria-hidden="true" /> {t('campaignLanding.customNote')}</p>
        </>
      ) : (
        <>
          {/* La constelación sale del catálogo de Fundamentales, no de la campaña: se pinta
              siempre que el catálogo traiga datos. */}
          {/* WP-F3 / CPE-158 — la OU puede no ofrecer la vista de Fundamentales. Va DENTRO de la
              rama del catálogo y no envolviendo el ternario: sacándolo fuera, una OU que la apagara
              perdería también el aviso honesto de «el catálogo está vacío». */}
          {catalogEmpty ? (
            <p className="rp-note">{t('catalog.empty')}</p>
          ) : (
            verFundamentales && <Constelacion funds={constelacionFunds} lang={lang} />
          )}

          {/* BUG (QA, 2026-08-23) — el nivel 4 de CampaignDocs.tsx (documentos CORPORATIVOS de la
              OU) hacía que, al borrar los documentos propios de la campaña, la sección siguiera
              mostrando 2 tarjetas sin que QA supiera de dónde salían (no eran datos corruptos: era
              el fallback corporativo funcionando como estaba diseñado — Tarea 16). Decisión del
              cliente al enterarse: en Brillamos Juntos, sin documentos propios de la campaña la
              sección debe ocultarse por completo, `allowCorporateFallback={false}` en los DOS call
              sites de este archivo (ver el otro, arriba, en la rama de campaña Personalizada). */}
          <CampaignDocs campaignId={campaign?.id} programa="Brillamos" isPersonalizada={isPersonalizada} allowCorporateFallback={false} />

          {campaign && verLeaderboard && rankingVisible && (
            // REQ-117 — leaderboard de nominaciones por OU/país (degradación honesta si no hay datos).
            // WP-F3/CPE-158: su OU puede no ofrecerlo. Es el único de los doce que nace APAGADO.
            // CPE-138: una excepción de localidad activa puede además restringirlo (rankingVisible).
            <CampaignOuLeaderboard campaignId={campaign.id} lang={lang} userOu={userOu} />
          )}

          {/* El panel de reconocer y el muro son HERMANOS de las demás secciones dentro del
              mismo `.dashboard-root` (demo : 656-714). Antes colgaban de un segundo
              `.shine-home > .dashboard-root` anidado, que reabría el contenedor y les daba un
              ancho y unos márgenes distintos a los del resto de la pantalla.
              Tampoco dependen de la campaña: `ReconocerPanel` no la usa para nada (es el mismo
              panel de /app/reconocer) y el muro solo la usa como FILTRO. Sin campaña se pintan
              igual, con el muro sin filtrar por campaña. Misma regla que el CTA del hero: con
              campaña se exige que esté activa, porque una finalizada no invita a reconocer. */}
          {(!campaign || active) && (
            <>
              {/* RN-PI03 — selector + panel de reconocer, con la modalidad PROPIA de Brillamos. Extraído
                  a su propio componente para mantener acotada la complejidad de CampaignLanding. */}
              <BrillamosRecognizeSection
                campaignId={campaign?.id ?? null}
                canBulk={canBulk}
                onOpenBulk={onOpenBulk}
                onSent={() => setShowSparkle(true)}
              />
              <section className="dash-card dash-card--moodboard shine-card shine-recos">
                <div className="moodboard-header">
                  <h2 className="moodboard-title shine-moodboard-title">{t('home.recent')}</h2>
                </div>
                <div className="shine-recos-scroll">
                  <div className="shine-recos-group">
                    <span className="shine-recos-label">{t('home.sent')}</span>
                    {/* QA (2026-08-28) — antes subía a 6 sin "Recibidos" (antiSpoiler o toggle de
                        campaña apagado), para llenar con contenido real el alto sobrante que deja
                        `flex:1` en este `.moodboard-grid`. Eso topaba la vista a un total de 6 y
                        generaba una paginación rara (bug reportado). Ahora el tope es SIEMPRE 3;
                        `align-content: start` (recognition.css) ya evita que el sobrante deforme
                        las filas — el espacio vacío que queda es solo aire. */}
                    <PostItWall title={t('home.sent')} titleIcon="sent" pageSize={3} baseParams={{ soloVigentes: true, emisorId: userId, programa: 'Brillamos' }} />
                  </div>
                  {/* BUG-119 — el separador va DENTRO del gate: sin él, apagar «recibidos» dejaba
                      una línea divisoria suelta después de «Enviados», separando de la nada.
                      Anti-Spoiler por campaña (2026-09-23): ya no gatea la sección, el servidor filtra. */}
                  {verRecibidosRecientes && (
                    <>
                      <div className="shine-recos-divider" />
                      <div className="shine-recos-group">
                        <span className="shine-recos-label">{t('home.received')}</span>
                        <PostItWall title={t('home.received')} titleIcon="received" pageSize={3} baseParams={{ soloVigentes: true, receptorId: userId, programa: 'Brillamos' }} />
                      </div>
                    </>
                  )}
                </div>
              </section>
            </>
          )}
        </>
      )}

      {/* `programa` es el MISMO con el que se habilitó el botón (useRecognitionPermissions('Brillamos')):
          el wizard revalida en el backend y sin él un lote sin campaña se validaba contra Fundamentales
          —el doc global—, así que el preview moría con 403 donde el botón sí estaba encendido. */}
      {showBulk && (
        <BulkUploadModal
          onClose={() => setShowBulk(false)}
          campaignId={campaign?.id ?? null}
          programa="Brillamos"
        />
      )}
      {showSparkle && <SparkleOverlay onDone={() => setShowSparkle(false)} />}
    </>
  );
}

function BrillamosContent() {
  const { i18n } = useTranslation('recognition');
  const lang = i18n.language;
  const { demoUser } = useUserContext();
  const ou = demoUser.ou || undefined;

  const campaign = useActiveCampaign(ou);
  const catalog = useRecognitionCatalog();
  const funds = catalog.data ?? [];


  return (
    <StateBlock
      isLoading={campaign.isLoading || catalog.isLoading}
      // El catálogo entra en `isError` a propósito. Antes sólo se miraba `campaign`, así que con la
      // campaña OK y el catálogo caído la vista no mostraba error NI spinner: pintaba con
      // `funds = []`, o sea una pantalla en blanco sin explicación. Es justo lo que veía un
      // colaborador Frontline (el catálogo iba por el cliente antiguo, sin token → 401) y lo que se
      // reportó como «se queda cargando y no se ve nada». Fallar visible es mejor que fallar mudo:
      // ese estado ofrece además el botón de reintento, que ya reintentaba ambas queries.
      isError={campaign.isError || catalog.isError}
      error={campaign.error ?? catalog.error}
      // Sin campaña activa NO es error ni vacío: la pantalla se pinta igual, sin las secciones
      // que dependen de la campaña.
      isEmpty={false}
      onRetry={() => { campaign.refetch(); catalog.refetch(); }}
    >
      {/* Sin campaña activa la pantalla YA NO cae a la Librería de Fundamentales: el cliente no
          quiere ver esa sección aquí (tiene su propia entrada de menú, `/app/fundamentales`) y el
          prototipo tampoco la pinta en "Brillamos Juntos". Se muestra siempre el home
          ShineTogether; lo que necesita campaña se omite. */}
      <CampaignLanding
        campaign={campaign.data ?? null}
        funds={funds}
        catalogEmpty={!catalog.isLoading && funds.length === 0}
        lang={lang}
        userId={demoUser.userId || undefined}
        userOu={ou}
      />
    </StateBlock>
  );
}

export function BrillamosView() {
  // F8 (H-101, "Brillamos siempre oscuro") — el demo fuerza body.dark mientras se ve esta
  // vista, sin importar el toggle de tema del usuario, para que el sidebar no desentone con
  // el starfield (PlataformaPage.tsx : 35-42). Se aplica a TODA la vista (con o sin campaña
  // activa — el demo tampoco distingue: es `recoView === 'brillamos'`, no una condición de la
  // campaña). Ver useForceDarkMode en contexts/ThemeContext.tsx.
  useForceDarkMode(true, 'brillamos');

  // `wide`: la landing de campaña se centra a ~1080px como el demo, en vez de heredar el
  // margen de 300px que `.home-dashboard-area` reserva para el riel de Fundamentales.
  //
  // `shine-home shine-home--embedded`: wrapper de tema del prototipo (demo
  // ShineTogetherHomePage.tsx : 601, variante `embedded` porque la pantalla vive dentro del
  // armazón con menú lateral). Es lo que activa el bloque "SHINE TOGETHER HOME — DARK STARFIELD
  // THEME" de recognition.css: cristal oscuro de las tarjetas, CTA en degradado violeta, panel de
  // reconocer y constelación en oscuro, y el centrado a 1080px con aire lateral.
  //
  // El starfield se monta como fondo de TODA la ruta: el prototipo pinta "Brillamos Juntos"
  // siempre sobre el cielo estrellado, haya campaña o no.
  return (
    <RecognitionShell wide className="shine-home shine-home--embedded" backdrop={<BrillamosStars />}>
      <BrillamosContent />
    </RecognitionShell>
  );
}

export default BrillamosView;
